
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useState } from "react";

interface ProductTabsProps {
  description: string;
  features?: string[];
  specifications?: string[];
  isHDBaseT?: boolean;
}

export const ProductTabs = ({
  description,
  features,
  specifications,
  isHDBaseT = false
}: ProductTabsProps) => {
  const { t, currentLanguage } = useLanguage();
  
  // Now we'll actually translate the content
  const [translatedDescription, setTranslatedDescription] = useState<string>(description);
  const [translatedFeatures, setTranslatedFeatures] = useState<string[]>(features || []);
  const [translatedSpecifications, setTranslatedSpecifications] = useState<string[]>(specifications || []);
  
  // Translate tab titles
  const overviewTitle = isHDBaseT ? t("full.description") : t("overview");
  const featuresTitle = t("features");
  const specificationsTitle = t("specifications");
  
  useEffect(() => {
    console.log("Translating content for language:", currentLanguage);
    
    // For description, attempt direct translation
    setTranslatedDescription(t(description));
    
    // For features, translate each item
    if (features && features.length > 0) {
      const translatedFeats = features.map(feature => t(feature));
      setTranslatedFeatures(translatedFeats);
    }
    
    // For specifications, translate each item
    if (specifications && specifications.length > 0) {
      const translatedSpecs = specifications.map(spec => t(spec));
      setTranslatedSpecifications(translatedSpecs);
    }
    
  }, [description, features, specifications, t, currentLanguage]);
  
  const createMarkup = (html: string) => {
    const linkedText = html.replace(
      /(https?:\/\/[^\s]+)/g, 
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>'
    );
    
    return { __html: linkedText };
  };

  // Determine if we should show the specifications tab
  const showSpecifications = specifications && specifications.length > 0;

  return (
    <Tabs defaultValue="overview" className="w-full">
      <TabsList className={`grid w-full ${showSpecifications ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <TabsTrigger value="overview">{overviewTitle}</TabsTrigger>
        <TabsTrigger value="features">{featuresTitle}</TabsTrigger>
        {showSpecifications && <TabsTrigger value="specifications">{specificationsTitle}</TabsTrigger>}
      </TabsList>
      <TabsContent value="overview">
        <Card className="p-6">
          <div 
            className="prose max-w-none text-muted-foreground"
            dangerouslySetInnerHTML={createMarkup(translatedDescription)}
          />
        </Card>
      </TabsContent>
      <TabsContent value="features">
        <Card className="p-6">
          <ul className="list-inside list-disc space-y-2">
            {translatedFeatures.map((feature, index) => (
              <li key={index} dangerouslySetInnerHTML={createMarkup(feature)} />
            ))}
          </ul>
        </Card>
      </TabsContent>
      {showSpecifications && (
        <TabsContent value="specifications">
          <Card className="p-6">
            <ul className="list-inside list-disc space-y-2">
              {translatedSpecifications.map((spec, index) => (
                <li key={index} dangerouslySetInnerHTML={createMarkup(spec)} />
              ))}
            </ul>
          </Card>
        </TabsContent>
      )}
    </Tabs>
  );
};
